import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ColumnDef,
  getCoreRowModel,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { Info } from 'lucide-react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { displayToastSuccess } from '@/helpers/display-toast';
import { useResizableColumns } from '@/hooks/use-resizable-columns';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { TableIds } from '@/lib/constants/table-ids';
import { CustomAttributeType } from '@/modules/common/types/custom-attribute';
import { compareFormValues } from '@/utils/form-state.utils';

import { CustomAttributesTable } from '../../components/custom-attributes/custom-attributes-table';
import { DeleteCustomAttributeModal } from '../../components/custom-attributes/delete-custom-attribute-modal';
import { SettingsPageWrapper } from '../../components/settings-page-wrapper';
import { getCustomAttributesColumns } from '../../constants/custom-attributes-table-columns';
import { useCustomAttributesSettings } from '../../hooks/custom-attributes/use-custom-attributes-settings';
import { useSaveCustomAttributes } from '../../hooks/custom-attributes/use-save-custom-attributes';
import {
  buildCustomAttributesSaveDiff,
  isDraftId,
  mapAttributesToFormRows,
} from '../../mappers/custom-attribute.mapper';
import { SettingsOutletContext } from '../../types/settings-footer';
import {
  CustomAttributeRowFormData,
  CustomAttributesFormData,
  customAttributesFormSchema,
} from '../../validations/custom-attribute-validation';

const I18N = 'pages.membersCustomAttributes';

// Monotonically decreasing temp id generator for unsaved draft rows.
let nextTempId = -1;

export const MembersCustomAttributesPage = () => {
  const { t } = useTranslation('authenticated.settings');
  const { setFooterState, setContentMaxWidth } = useOutletContext<SettingsOutletContext>();

  const { data: attributes, isLoading, error, refetch } = useCustomAttributesSettings();
  const saveMutation = useSaveCustomAttributes();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [attributeToDelete, setAttributeToDelete] = useState<CustomAttributeRowFormData | null>(
    null
  );

  // Snapshot of the loaded data — used to detect true changes and to build the save diff.
  const initialDataRef = useRef<CustomAttributeRowFormData[]>([]);

  const defaultValues = useMemo<CustomAttributesFormData>(
    () => ({ attributes: mapAttributesToFormRows(attributes) }),
    [attributes]
  );

  const form = useForm<CustomAttributesFormData>({
    resolver: zodResolver(customAttributesFormSchema),
    mode: 'onChange',
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'attributes',
  });

  const unsavedChangesGuard = useUnsavedChangesGuard({
    isDirty: form.formState.isDirty,
    onDiscard: () => form.reset(),
  });

  // Wide content area to fit name + type + options + action columns comfortably.
  useEffect(() => {
    setContentMaxWidth('max-w-5xl');
    return () => setContentMaxWidth(undefined);
  }, [setContentMaxWidth]);

  // Reset the form whenever fresh data arrives (initial load + post-save refetch).
  useEffect(() => {
    if (attributes) {
      const formRows = mapAttributesToFormRows(attributes);
      initialDataRef.current = formRows;
      form.reset({ attributes: formRows });
    }
  }, [attributes, form]);

  const handleSave = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const diff = buildCustomAttributesSaveDiff(form.getValues().attributes, initialDataRef.current);
    if (diff.create.length === 0 && diff.update.length === 0) return;

    try {
      await saveMutation.mutateAsync(diff);
      // No explicit refetch: the mutation's onSuccess invalidates the shared
      // ['custom-attributes'] query (refetchType: 'all'), which refreshes this list and
      // resets the form via the `attributes` effect. Refetching here too would fire a
      // second redundant GET.
      displayToastSuccess(t(`${I18N}.saveSuccess`));
    } catch {
      // Error surfaced by the mutation's onError handler.
    }
  }, [form, saveMutation, t]);

  // Live snapshot of the form rows. Subscribing here re-renders the page on every edit
  // (name, type, or option change) so the footer effect below recomputes — relying on
  // `formState.isDirty` alone is not enough because it latches `true` after the first edit.
  const watchedAttributes = useWatch({ control: form.control, name: 'attributes' });

  // Duplicate-name is a cross-row rule, so editing one row's name must re-validate the
  // others (RHF's onChange mode only validates the field that changed). Whenever any name
  // changes we re-trigger validation for every row's name field so a newly-created
  // duplicate is flagged — and a resolved one is cleared — live. `trigger` only updates
  // errors, never values, so this cannot loop with the watch above.
  //
  // JSON-encode the names so two arrangements can never collapse to the same
  // signature (a plain join could: ["a b","c"] and ["a","b c"] both join to "a b c").
  const nameSignature = JSON.stringify((watchedAttributes ?? []).map(row => row?.name ?? ''));

  useEffect(() => {
    if (!watchedAttributes?.length) return;
    const namePaths = watchedAttributes.map((_, index) => `attributes.${index}.name` as const);
    void form.trigger(namePaths);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameSignature]);

  useEffect(() => {
    const hasChanges = compareFormValues(watchedAttributes, initialDataRef.current);
    const isValid = form.formState.isValid;

    setFooterState({
      isVisible: true,
      isEnabled: hasChanges && isValid,
      isLoading: saveMutation.isPending,
      onSave: handleSave,
      // Explain the block only when the user has made changes but validation fails — not
      // when the button is merely inactive because nothing has changed yet.
      disabledReason: hasChanges && !isValid ? t(`${I18N}.saveDisabledReason`) : undefined,
    });
    // `handleSave` and `setFooterState` are intentionally omitted: `handleSave` gets a new
    // identity every render (its `saveMutation` dep is a fresh object from useMutation), so
    // including it would re-run this effect on every render and loop with the setFooterState
    // → parent re-render cycle. The stale closure is still correct because it reads live
    // state via form.getValues(). This matches the departments / external-rates pages.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedAttributes, form.formState.isValid, saveMutation.isPending]);

  useEffect(() => {
    return () => {
      setFooterState({
        isVisible: false,
        isEnabled: false,
        isLoading: false,
        onSave: () => {},
      });
    };
  }, [setFooterState]);

  const handleAddAttribute = () => {
    const newAttribute: CustomAttributeRowFormData = {
      attributeId: nextTempId--,
      name: '',
      type: CustomAttributeType.Select,
      options: [],
    };
    append(newAttribute);
  };

  const handleDeleteClick = useCallback(
    (attribute: CustomAttributeRowFormData, index: number) => {
      // Unsaved draft rows are removed locally with no backend call.
      if (isDraftId(attribute.attributeId)) {
        remove(index);
        return;
      }

      const shouldProceed = unsavedChangesGuard.checkUnsavedChanges(() => {
        setAttributeToDelete(attribute);
        setDeleteModalOpen(true);
      });

      if (shouldProceed) {
        setAttributeToDelete(attribute);
        setDeleteModalOpen(true);
      }
    },
    [remove, unsavedChangesGuard]
  );

  const handleDeleteSuccess = () => {
    // The delete mutation's onSuccess already invalidates the shared query, so the list
    // refreshes without an explicit refetch here.
    setDeleteModalOpen(false);
    setAttributeToDelete(null);
  };

  const columns = useMemo<ColumnDef<CustomAttributeRowFormData>[]>(
    () => getCustomAttributesColumns({ onDelete: handleDeleteClick, control: form.control, t }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { tableOptions: resizeOptions, saveColumnSizing } = useResizableColumns(
    TableIds.MEMBER_CUSTOM_ATTRIBUTES
  );

  const table = useReactTable({
    data: fields,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    ...resizeOptions,
  } as TableOptions<CustomAttributeRowFormData>);

  const unsavedChangesGuardProps = useMemo(
    () => ({
      isModalOpen: unsavedChangesGuard.isModalOpen,
      onDiscard: unsavedChangesGuard.handleModalDiscard,
      onCancel: unsavedChangesGuard.handleModalCancel,
    }),
    [
      unsavedChangesGuard.isModalOpen,
      unsavedChangesGuard.handleModalDiscard,
      unsavedChangesGuard.handleModalCancel,
    ]
  );

  return (
    <SettingsPageWrapper
      isLoading={isLoading}
      error={error}
      onRetry={() => {
        void refetch();
      }}
      unsavedChangesGuard={unsavedChangesGuardProps}
    >
      <Card className="p-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold leading-7">{t(`${I18N}.title`)}</h2>
        </div>

        <Alert variant="info" className="mb-4">
          <Info className="h-4 w-4" />
          <AlertDescription>{t(`${I18N}.alert`)}</AlertDescription>
        </Alert>

        <p className="mb-4 text-xs text-muted-foreground">
          {t(`${I18N}.allAttributes`, { count: attributes?.length || 0 })}
        </p>

        <Form {...form}>
          <form>
            <CustomAttributesTable
              table={table}
              onAddAttribute={handleAddAttribute}
              hasAttributes={fields.length > 0}
              isLoading={isLoading}
              tableId={TableIds.MEMBER_CUSTOM_ATTRIBUTES}
              onResizeEnd={saveColumnSizing}
            />
          </form>
        </Form>
      </Card>

      <DeleteCustomAttributeModal
        attribute={attributeToDelete}
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setAttributeToDelete(null);
        }}
        onSuccess={handleDeleteSuccess}
      />
    </SettingsPageWrapper>
  );
};
